/**
 * AI CONTEXT: mobile-first - Product catalog page
 * Journal: J-20250809-01
 * 
 * This component implements the product store with:
 * - Mobile-first responsive design
 * - Product filtering and search
 * - Shopping cart integration
 * - Tier-based product organization
 * - Touch-friendly interface elements
 * - Stable default image fallback consistent with Home/Giveaways
 * 
 * Architecture decisions:
 * - Use grid layout for product display
 * - Implement search and filter functionality
 * - Provide clear product information
 * - Follow mobile-first design principles
 * - Use `getAssetUrl` with single-shot onError fallback to prevent flicker
 * 
 * Future considerations:
 * - Add product categories and subcategories
 * - Implement advanced filtering options
 * - Add product comparison features
 * - Consider wishlist functionality
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ShoppingBag, Star } from 'lucide-react';
// import { getAssetUrl } from '../utils/assetUtils';
import S3Image from '../components/S3Image';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../contexts/ProductContext';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../i18n';
import BurstBags from '../components/BurstBags';
import { setSEO } from '../utils/seo';

const Store = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const initialTier = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('tier') || 'all';
    } catch (_) { return 'all'; }
  })();
  const [selectedTier, setSelectedTier] = useState(initialTier);
  const initialType = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('type') || 'all';
    } catch (_) { return 'all'; }
  })();
  const [selectedType, setSelectedType] = useState(initialType); // 'all' | 'subscription'
  const { addToCart } = useCart();
  const { products: apiProducts, loading } = useProducts();
  const { isAdmin } = useAuth();

  // Products come from API (or demo fallback in context)
  const products = apiProducts;

  const tiers = [
    { value: 'all', label: t('store.tier.all') },
    { value: 'Bronze', label: 'Bronze' },
    { value: 'Silver', label: 'Silver' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Platinum', label: 'Platinum' }
  ];

  // AI CONTEXT: business-logic - Product filtering
  // Reasoning: Allow users to find specific products
  // Constraints: Must handle search and tier filtering
  // Future: Add advanced filtering and sorting options

  const filteredProducts = useMemo(() => {
    const filtered = products.filter(product => {
      // Hide admin-only test products from non-admin customers
      if (product.adminOnly && !isAdmin) return false;

      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTier = selectedTier === 'all' || product.tier === selectedTier;
      const matchesType = selectedType === 'all' || (selectedType === 'subscription' && product.isSubscription);
      return matchesSearch && matchesTier && matchesType;
    });
    
    // Sort by display order: products with displayOrder come first, sorted numerically
    // Products without displayOrder come last, sorted by name
    return filtered.sort((a, b) => {
      const aOrder = a.displayOrder;
      const bOrder = b.displayOrder;
      
      // If both have display order, sort by it
      if (aOrder !== null && aOrder !== undefined && bOrder !== null && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      
      // If only one has display order, prioritize it
      if (aOrder !== null && aOrder !== undefined) return -1;
      if (bOrder !== null && bOrder !== undefined) return 1;
      
      // If neither has display order, sort by name
      return a.name.localeCompare(b.name);
    });
  }, [products, searchTerm, selectedTier, selectedType, isAdmin]);

  // AI CONTEXT: ux - Handle add to cart
  // Reasoning: Provide immediate feedback for cart actions
  // Constraints: Must integrate with cart context
  // Future: Add quantity selection and stock checking

  const [burstForProductId, setBurstForProductId] = useState(null);
  const [ambientTick, setAmbientTick] = useState(0);
  const handleAddToCart = useCallback((product) => {
    addToCart(product);
    setBurstForProductId(product.id);
    setTimeout(() => setBurstForProductId(null), 1000);
  }, [addToCart]);

  // Quick-add by id via ?quick=PRODUCT_ID and ensure tier filter matches link intent
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const quickId = params.get('quick');
      if (quickId && apiProducts && apiProducts.length > 0) {
        const target = apiProducts.find(p => String(p.id) === String(quickId));
        if (target) {
          handleAddToCart(target);
          // Remove quick param from URL to avoid duplicate adds on refresh
          params.delete('quick');
          const newUrl = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, '', newUrl);
        }
      }
    } catch (_e) {}
  }, [apiProducts, handleAddToCart]);

  // Subtle ambient background burst loop
  useEffect(() => {
    const id = setInterval(() => setAmbientTick((n) => n + 1), 8000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setSEO({
      title: 'Store — Tolbert Tactical',
      description: 'Browse premium tactical gear and subscriptions. Earn giveaway entries with every purchase.',
      path: '/store',
      image: '/files/Logo.png'
    });
  }, []);

  // AI CONTEXT: ux - Render star ratings
  // Reasoning: Provide visual product ratings
  // Constraints: Must handle decimal ratings
  // Future: Add detailed review system

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 fill-yellow-400 text-yellow-400" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }

    return stars;
  };

  const ProductGallery = ({ items, alt }) => {
    const [active, setActive] = useState(0);
    const images = Array.isArray(items) && items.length > 0 ? items : ['/files/Logo.png'];
    const main = images[Math.min(active, images.length - 1)];
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <S3Image
            s3KeyOrUrl={main}
            alt={alt}
            className="w-full h-full max-w-full max-h-full object-contain p-4"
          />
        </div>
        {images.length > 1 && (
          <div className="p-2 flex justify-center gap-2">
            {images.map((thumb, idx) => (
              <button
                key={thumb + idx}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActive(idx); }}
                className={`border rounded ${active === idx ? 'border-custom-orange' : 'border-theme-silver/30'}`}
                title={`Image ${idx + 1}`}
              >
                <S3Image s3KeyOrUrl={thumb} alt="thumb" className="w-10 h-10 object-contain" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-theme-black relative overflow-hidden">
      {/* Ambient faint background bursts */}
      <BurstBags triggerKey={ambientTick} className="absolute inset-0 opacity-40" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-theme-silver mb-4">{t('store.title')}</h1>
          <p className="text-lg text-theme-silver-light">{t('store.subtitle')}</p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-silver-light h-5 w-5" />
            <input
              type="text"
              placeholder={t('store.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 glass-input text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange/50"
            />
          </div>

          {/* Type + Tier Filters */}
          <div className="flex flex-wrap gap-2 mb-2">
            {[
              { value: 'all', label: 'All Products' },
              { value: 'subscription', label: 'Subscriptions' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSelectedType(opt.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedType === opt.value
                    ? 'bg-custom-orange text-white'
                    : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Tier Filter */}
          <div className="flex flex-wrap gap-2">
            {tiers.map((tier) => (
              <button
                key={tier.value}
                onClick={() => setSelectedTier(tier.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedTier === tier.value
                    ? 'bg-custom-orange text-white'
                    : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                }`}
              >
                {tier.label}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="glass-card-dark hover:shadow-glow transition-all duration-300 relative overflow-visible">
              <div className="aspect-square glass-card rounded-lg mb-4 overflow-hidden flex items-center justify-center">
                <ProductGallery items={Array.isArray(product.images) && product.images.length > 0 ? product.images : [product.image || '/files/Logo.png']} alt={product.name} />
              </div>
              
              <div className="p-6 relative">
                {/* Subtle burst overlay when added */}
                {burstForProductId === product.id && (
                  <BurstBags triggerKey={burstForProductId} className="absolute inset-0" />
                )}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-theme-silver">{product.name}</h3>
                  <span className={`tier-badge tier-${(product.tier || 'Bronze').toLowerCase()}`}>{product.tier}</span>
                </div>
                
                <p className="text-theme-silver-light mb-4 text-sm">{product.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl font-bold">
                    {product.salesPrice ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-custom-orange line-through">${product.price}</span>
                        <span className="text-theme-silver">${product.salesPrice}</span>
                      </div>
                    ) : (
                      <span className="text-theme-silver">${product.price}</span>
                    )}
                  </div>
                  <div className="text-right text-sm text-theme-silver-light">
                    <div>{product.entries} entry{product.entries > 1 ? 's' : ''}</div>
                    <div>{(product.credits ?? product.entries ?? 1)} credit{(product.credits ?? product.entries ?? 1) > 1 ? 's' : ''}</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-1">
                    {renderStars(product.rating)}
                    <span className="text-sm text-theme-silver-light ml-1">({product.reviews})</span>
                  </div>
                  <span className="text-sm text-theme-silver-light">
                    {product.inStock ? t('store.inStock') : t('store.outOfStock')}
                  </span>
                </div>
                
                <div className="flex space-x-2">
                  <Link
                    to={`/product/${product.id}`}
                    className="btn-secondary flex-1 text-center"
                  >
                    {t('store.viewDetails')}
                  </Link>
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.inStock}
                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('store.addToCart')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State / Loading */}
        {(loading || filteredProducts.length === 0) && (
          <div className="text-center py-12">
            <ShoppingBag className="h-16 w-16 text-theme-silver mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-theme-silver mb-2">{loading ? t('store.loading') : t('store.noneFound')}</h3>
            {!loading && (
              <>
                <p className="text-theme-silver-light mb-4">{t('store.adjustSearch')}</p>
                <button
                  onClick={() => { setSearchTerm(''); setSelectedTier('all'); }}
                  className="btn-primary"
                >
                  {t('store.clearFilters')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Store; 